import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { Country, State, LocalGovernmentArea, MapViewState } from '../../types/geography';
import { IncidentCount, THREAT_COLORS } from '../../services/incidents';

// Initialize Mapbox
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN!;

interface IncidentMapContainerProps {
  level: 'country' | 'state' | 'lga';
  countries: Country[];
  states: State[];
  lgas: LocalGovernmentArea[];
  incidentCounts: IncidentCount[];
  selectedCountry?: Country | null;
  selectedState?: State | null;
  selectedLga?: LocalGovernmentArea | null;
  onFeatureClick?: (feature: { id: string; name: string }) => void;
  className?: string;
}

// Predefined view states
const MAP_VIEWS: Record<string, MapViewState> = {
  WORLD: { longitude: 0, latitude: 20, zoom: 2 },
  AFRICA: { longitude: 20, latitude: 5, zoom: 3 },
  WEST_AFRICA: { longitude: -2.5, latitude: 9.5, zoom: 4 },
  NIGERIA: { longitude: 8.0, latitude: 9.0, zoom: 6 },
  CANADA: { longitude: -106.0, latitude: 56.0, zoom: 3.5 },
  NORTH_AMERICA: { longitude: -100, latitude: 45, zoom: 3 }
};

const IncidentMapContainer: React.FC<IncidentMapContainerProps> = ({
  level,
  countries,
  states,
  lgas,
  incidentCounts,
  selectedCountry,
  selectedState,
  selectedLga,
  onFeatureClick,
  className = "w-full h-96"
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Keep track of the latest onFeatureClick handler without triggering re-renders
  const onFeatureClickRef = useRef(onFeatureClick);
  useEffect(() => {
    onFeatureClickRef.current = onFeatureClick;
  }, [onFeatureClick]);

  // Build heatmap from incident counts
  const incidentHeatmap = new Map<string, { count: number; threatLevel: number }>();
  incidentCounts.forEach(item => {
    incidentHeatmap.set(item.area_id, {
      count: item.incident_count,
      threatLevel: item.threat_level || 1
    });
  });

  // Get color based on threat level
  const getThreatColor = (level: number): string => {
    const safeLevel = Math.max(1, Math.min(12, Math.round(level)));
    return THREAT_COLORS[safeLevel] || THREAT_COLORS[1];
  };

  // Initialize map with admin boundaries style
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    try {
      // Ensure Mapbox CSS is loaded
      if (!document.querySelector('link[href*="mapbox-gl.css"]')) {
        const link = document.createElement('link');
        link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }

      const initialView = MAP_VIEWS.CANADA;
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [initialView.longitude, initialView.latitude],
        zoom: initialView.zoom
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      map.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');

      map.current.on('load', () => {
        setIsMapLoaded(true);
        setIsLoading(false);
      });

      map.current.on('error', (e) => {
        console.error('Map error:', e);
        setError('Failed to load map');
        setIsLoading(false);
      });

    } catch (err) {
      console.error('Map init error:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize map');
      setIsLoading(false);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update map layers when data changes
  useEffect(() => {
    if (!isMapLoaded || !map.current) return;

    // Clear existing layers
    const layersToRemove = ['areas-labels', 'areas-border', 'areas-fill'];
    const sourcesToRemove = ['areas'];

    layersToRemove.forEach(layerId => {
      if (map.current!.getLayer(layerId)) {
        map.current!.removeLayer(layerId);
      }
    });

    sourcesToRemove.forEach(sourceId => {
      if (map.current!.getSource(sourceId)) {
        map.current!.removeSource(sourceId);
      }
    });

    // Build features based on level
    let features: GeoJSON.Feature[] = [];
    
    // Create a more robust lookup map
    const incidentHeatmap = new Map();
    const incidentNameMap = new Map(); // Fallback map by name
    
    incidentCounts.forEach(item => {
      if (item.area_id) incidentHeatmap.set(item.area_id.toLowerCase().trim(), item);
      if (item.area_name) incidentNameMap.set(item.area_name.toLowerCase().trim(), item);
    });

    const createFeature = (item: any, index: number) => {
      // Try ID match first (robust)
      let incidentData = incidentHeatmap.get(item.id?.toLowerCase().trim());
      
      // Fallback: match by name if ID match fails
      if (!incidentData && item.name) {
        incidentData = incidentNameMap.get(item.name.toLowerCase().trim());
      }

      const threatLevel = incidentData?.threat_level || 1;
      // Use same 12-level color scale as incident-map page for consistency
      const safeLevel = Math.max(1, Math.min(12, Math.round(threatLevel)));
      const color = THREAT_COLORS[safeLevel] || THREAT_COLORS[1];

      return {
        type: 'Feature' as const,
        id: index,
        properties: {
          id: item.id,
          name: item.name,
          incident_count: incidentData?.incident_count || 0,
          threat_level: threatLevel,
          color: color
        },
        geometry: item.geometry as GeoJSON.Geometry
      };
    };

    if (level === 'country') {
      features = countries
        .filter(item => item.geometry)
        .map(createFeature);
    } else if (level === 'state') {
      features = states
        .filter(item => item.geometry)
        .map(createFeature);
    } else {
      features = lgas
        .filter(item => item.geometry)
        .map(createFeature);
    }

    if (features.length === 0) return;
    
    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features
    };

    if (geojson.features.length === 0) return;

    // Add source
    map.current.addSource('areas', {
      type: 'geojson',
      data: geojson
    });

    // Add fill layer with dynamic coloring
    map.current.addLayer({
      id: 'areas-fill',
      type: 'fill',
      source: 'areas',
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          0.8,
          0.7
        ]
      }
    });

    // Add border layer
    map.current.addLayer({
      id: 'areas-border',
      type: 'line',
      source: 'areas',
      paint: {
        'line-color': '#374151',
        'line-width': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          2,
          1
        ]
      }
    });

    // Add text labels for areas (visible at appropriate zoom levels)
    map.current.addLayer({
      id: 'areas-labels',
      type: 'symbol',
      source: 'areas',
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          6, 10,   // At zoom 6, text size 10
          8, 12,   // At zoom 8, text size 12
          10, 14,  // At zoom 10, text size 14
          12, 16   // At zoom 12, text size 16
        ],
        'text-variable-anchor': ['center'],
        'text-justify': 'center',
        'text-allow-overlap': false,
        'text-ignore-placement': false
      },
      paint: {
        'text-color': '#1f2937',
        'text-halo-color': '#ffffff',
        'text-halo-width': 2,
        'text-halo-blur': 1
      },
      minzoom: level === 'lga' ? 8 : level === 'state' ? 6 : 4
    });

    // Add hover effects
    let hoveredFeatureId: number | null = null;

    map.current.on('mouseenter', 'areas-fill', (e) => {
      map.current!.getCanvas().style.cursor = 'pointer';
      
      if (e.features && e.features.length > 0) {
        if (hoveredFeatureId !== null) {
          map.current!.setFeatureState(
            { source: 'areas', id: hoveredFeatureId },
            { hover: false }
          );
        }
        
        hoveredFeatureId = e.features[0].id as number;
        map.current!.setFeatureState(
          { source: 'areas', id: hoveredFeatureId },
          { hover: true }
        );
      }
    });

    // Update tooltip on mouse move
    map.current.on('mousemove', 'areas-fill', (e) => {
      if (e.features && e.features.length > 0) {
        const props = e.features[0].properties;
        const tooltip = document.getElementById('map-tooltip');
        if (tooltip) {
          const areaType = level === 'lga' ? 'LGA' : level === 'state' ? 'State' : 'Country';
          const threatLevel = props?.threat_level || 1;
          
          let threatLabel = 'Low';
          if (threatLevel >= 10) threatLabel = 'Extreme';
          else if (threatLevel >= 7) threatLabel = 'High';
          else if (threatLevel >= 4) threatLabel = 'Medium';
          else if (threatLevel >= 3) threatLabel = 'Low-Medium';

          tooltip.innerHTML = `
            <div class="font-semibold text-gray-900">${props?.name}</div>
            <div class="text-xs text-gray-600 mt-1">${areaType}</div>
            <div class="text-xs mt-1">
              <span class="font-medium">Incidents:</span> ${props?.incident_count || 0}
            </div>
            <div class="text-xs mt-1 flex items-center gap-2">
              <span class="font-medium">Threat Level:</span> 
              <span class="px-1.5 py-0.5 rounded text-white text-xs font-bold" style="background-color: ${props?.color || '#22c55e'}">
                ${threatLevel}/12 (${threatLabel})
              </span>
            </div>
          `;
          tooltip.style.display = 'block';
          tooltip.style.left = `${e.point.x + 10}px`;
          tooltip.style.top = `${e.point.y + 10}px`;
        }
      }
    });

    map.current.on('mouseleave', 'areas-fill', () => {
      map.current!.getCanvas().style.cursor = '';
      
      if (hoveredFeatureId !== null) {
        map.current!.setFeatureState(
          { source: 'areas', id: hoveredFeatureId },
          { hover: false }
        );
      }
      hoveredFeatureId = null;

      const tooltip = document.getElementById('map-tooltip');
      if (tooltip) {
        tooltip.style.display = 'none';
      }
    });

    // Add click handler
    map.current.on('click', 'areas-fill', (e) => {
      if (e.features && e.features[0] && onFeatureClickRef.current) {
        const props = e.features[0].properties;
        onFeatureClickRef.current({
          id: props?.id || '',
          name: props?.name || ''
        });
      }
    });

  }, [isMapLoaded, level, countries, states, lgas, incidentCounts]);

  // Fly to selected area with zoom to geometry bounds
  useEffect(() => {
    if (!isMapLoaded || !map.current) return;

    let targetView: MapViewState | null = null;
    let targetGeometry: unknown = null;

    // Priority: LGA > State > Country
    if (selectedLga?.geometry) {
      targetGeometry = selectedLga.geometry;
    } else if (selectedState?.geometry) {
      targetGeometry = selectedState.geometry;
    } else if (selectedCountry?.geometry) {
      targetGeometry = selectedCountry.geometry;
    } else if (selectedCountry) {
      // Fallback to predefined views if no geometry
      if (selectedCountry.code === 'NGA') {
        targetView = MAP_VIEWS.NIGERIA;
      } else if (selectedCountry.code === 'CAN') {
        targetView = MAP_VIEWS.CANADA;
      } else if (selectedCountry.code === 'GHA') {
        targetView = { longitude: -1.0, latitude: 7.9, zoom: 6 };
      }
    }

    if (targetGeometry) {
      // Calculate bounds from geometry and fit to it
      const bounds = calculateBounds(targetGeometry);
      if (bounds) {
        try {
          map.current.fitBounds(bounds, { 
            padding: { top: 80, bottom: 80, left: 80, right: 80 },
            duration: 1200,
            maxZoom: selectedLga ? 11 : selectedState ? 9 : 7,
            essential: true
          });
        } catch (err) {
          console.warn('Failed to fit bounds:', err);
        }
      }
    } else if (targetView) {
      map.current.flyTo({
        center: [targetView.longitude, targetView.latitude],
        zoom: targetView.zoom,
        duration: 1200,
        essential: true
      });
    }
  }, [isMapLoaded, selectedCountry, selectedState, selectedLga]);

  // Calculate bounds from GeoJSON geometry
  const calculateBounds = (geometry: any): [[number, number], [number, number]] | null => {
    if (!geometry || !geometry.coordinates) return null;

    try {
      let allCoords: number[][] = [];

      // Extract all coordinates from the geometry
      const extractCoords = (coords: any) => {
        if (Array.isArray(coords)) {
          if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
            // This is a [lng, lat] pair
            allCoords.push(coords);
          } else {
            // Recursively extract from nested arrays
            coords.forEach(extractCoords);
          }
        }
      };

      extractCoords(geometry.coordinates);

      if (allCoords.length === 0) return null;

      const lngs = allCoords.map(c => c[0]).filter(lng => lng >= -180 && lng <= 180);
      const lats = allCoords.map(c => c[1]).filter(lat => lat >= -90 && lat <= 90);

      if (lngs.length === 0 || lats.length === 0) return null;

      return [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)]
      ];
    } catch (err) {
      console.error('Error calculating bounds:', err);
      return null;
    }
  };

  if (error) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center rounded-lg`}>
        <div className="text-center">
          <div className="text-red-500 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600 font-medium">Failed to load map</p>
          <p className="text-gray-500 text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} relative`}>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
            <p className="text-gray-600 text-sm">Loading map...</p>
          </div>
        </div>
      )}
      <div ref={mapContainer} className="w-full h-full" />
      
      {/* Tooltip */}
      <div
        id="map-tooltip"
        className="absolute bg-white px-3 py-2 rounded shadow-lg text-sm pointer-events-none z-20"
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default IncidentMapContainer;
